/**
 * Durable Updates inbox. Tap a lock-screen alert to land on the update itself.
 * Each category renders the facts that help on that screen. Seen is not Done.
 */

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import type { InboxAlert } from "@shared/push.ts";
import {
  inboxPath,
  presentAlert,
  tdacTravellerLabel,
  type AlertHero,
  type AlertPresentation,
  type AlertStop,
} from "@shared/alert-inbox.ts";
import { TDAC_OFFICIAL_URL } from "@shared/tdac.ts";
import { confirmTestPush, fetchTdac, fetchUpdates, markUpdateSeen, readDeviceId, saveTdac } from "../../lib/push.ts";
import { useTrip } from "../../app/trip.tsx";
import { SmartBack } from "../../components/SmartBack.tsx";
import { FactRow } from "../../components/FactRow.tsx";

function HeroBlock({ hero }: { hero: AlertHero }) {
  if (!hero) return null;
  if (hero.kind === "change") {
    const short = hero.from.length <= 12 && hero.to.length <= 12;
    if (!short) {
      return (
        <>
          <FactRow label="Was" value={hero.from} />
          <FactRow label="Now" value={hero.to} />
        </>
      );
    }
    return (
      <div className="inbox-change" aria-label="What changed">
        <div>
          <p className="inbox-kicker">Was</p>
          <p className="inbox-change-value tabular">{hero.from}</p>
        </div>
        <p className="inbox-change-to">to</p>
        <div>
          <p className="inbox-kicker">Now</p>
          <p className="inbox-change-value tabular">{hero.to}</p>
        </div>
      </div>
    );
  }
  if (hero.kind === "amount") {
    return (
      <div className="inbox-hero-panel" aria-label="Amount">
        <p className="inbox-change-value tabular">{hero.value}</p>
        <p className="muted">{hero.caption}</p>
      </div>
    );
  }
  if (hero.kind === "count") {
    return (
      <div className="inbox-hero-panel" aria-label="Remaining">
        <p className="inbox-change-value tabular">{hero.value}</p>
        <p className="muted">{hero.caption}</p>
      </div>
    );
  }
  if (hero.kind === "time") {
    return (
      <div className="inbox-hero-panel" aria-label="Time">
        <p className="inbox-change-value tabular">{hero.value}</p>
        <p className="muted">{hero.caption}</p>
      </div>
    );
  }
  return (
    <section className="card board inbox-board" aria-label="Route">
      <div className="leg">
        {hero.fromTime ? <p className="time tabular">{hero.fromTime}</p> : null}
        <p>{hero.from}</p>
      </div>
      <div className="mid">
        <p>to</p>
      </div>
      <div className="leg arrive">
        {hero.toTime ? <p className="time tabular">{hero.toTime}</p> : null}
        <p>{hero.to}</p>
      </div>
    </section>
  );
}

function StopList({ stops }: { stops: AlertStop[] }) {
  if (!stops.length) return null;
  return (
    <ol className="inbox-stops">
      {stops.map((stop) => (
        <li key={`${stop.time ?? ""}:${stop.title}`}>
          <p className="time tabular">{stop.time ?? "Flexible"}</p>
          {stop.href ? <Link to={stop.href}>{stop.title}</Link> : <p>{stop.title}</p>}
        </li>
      ))}
    </ol>
  );
}

function InboxRow({
  alert,
  unread,
}: {
  alert: InboxAlert;
  unread: boolean;
}) {
  const { trip } = useTrip();
  const view = presentAlert(alert, trip ?? null);
  return (
    <Link
      className={`inbox-row ${alert.severity} ${unread ? "unread" : ""} ${alert.status}`}
      to={inboxPath(alert.id)}
    >
      <span className="inbox-rail" aria-hidden="true" />
      <span className="inbox-row-copy">
        <span className="inbox-kicker">{view.kicker}</span>
        <span className="inbox-title">{view.title}</span>
        <span className="inbox-line">{view.preview}</span>
      </span>
      <span className="inbox-when tabular">{unread ? "New" : view.when}</span>
    </Link>
  );
}

function TdacChecklist({
  remaining,
  complete,
  task,
  trip,
  onToggle,
}: {
  remaining?: number;
  complete?: boolean;
  task?: { expectedTravellerKeys: string[]; completedTravellerKeys: string[] } | null;
  trip: ReturnType<typeof useTrip>["trip"];
  onToggle: (key: string, done: boolean) => void;
}) {
  return (
    <div className="inbox-tdac">
      <p>
        {complete
          ? "Every listed traveller is marked done on this phone. That is still not an official immigration status."
          : `${remaining ?? 0} traveller${(remaining ?? 0) === 1 ? "" : "s"} still need a card. Submit on the official portal.`}
      </p>
      <div className="inbox-travellers">
        {(task?.expectedTravellerKeys ?? []).map((key) => {
          const done = task?.completedTravellerKeys.includes(key);
          return (
            <label key={key} className={`check-row ${done ? "done" : ""}`}>
              <input
                type="checkbox"
                checked={Boolean(done)}
                onChange={() => onToggle(key, !done)}
              />
              <span>
                <strong>{tdacTravellerLabel(key, trip)}</strong>
                <span className="muted"> {done ? "marked done" : "still to file"}</span>
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function AlertActions({
  view,
  unread,
  confirmNote,
  onConfirmTest,
  onSeen,
}: {
  view: AlertPresentation;
  unread: boolean;
  confirmNote: string | null;
  onConfirmTest: () => void;
  onSeen: () => void;
}) {
  return (
    <>
      {confirmNote ? <p className="banner">{confirmNote}</p> : null}
      <div className="row-actions">
        {view.confirmTest ? (
          <button className="btn" type="button" onClick={onConfirmTest}>I saw this on the lock screen</button>
        ) : null}
        {view.action ? (
          view.action.external ? (
            <a className="btn" href={view.action.href} rel="noreferrer" target="_blank">{view.action.label}</a>
          ) : (
            <Link className="btn" to={view.action.href}>{view.action.label}</Link>
          )
        ) : null}
        {view.secondary ? <Link className="btn-secondary" to={view.secondary.href}>{view.secondary.label}</Link> : null}
        {unread ? (
          <button className="btn-ghost" type="button" onClick={onSeen}>Mark seen</button>
        ) : null}
      </div>
    </>
  );
}

function AlertDetail({
  alert,
  unread,
  tdac,
  onSeen,
  onConfirmTest,
  onTdac,
  confirmNote,
}: {
  alert: InboxAlert;
  unread: boolean;
  tdac: { remaining?: number; complete?: boolean; task?: { expectedTravellerKeys: string[]; completedTravellerKeys: string[] } } | null;
  onSeen: () => void;
  onConfirmTest: () => void;
  onTdac: (key: string, done: boolean) => void;
  confirmNote: string | null;
}) {
  const { trip } = useTrip();
  const view = presentAlert(alert, trip ?? null);

  return (
    <article className={`inbox-detail layout-${view.layout} ${alert.severity}`}>
      <p className="inbox-kicker">{view.kicker}</p>
      <h1 className="page-title">{view.title}</h1>
      {unread && view.layout !== "test" ? <p className="inbox-unread-flag">Not marked seen on this phone</p> : null}
      {view.statusNote ? <p className="banner warn">{view.statusNote}</p> : null}
      {view.summary ? <p className="lead">{view.summary}</p> : null}

      <HeroBlock hero={view.hero} />

      {view.meeting ? (
        <section className="card pickup-panel">
          <p className="inbox-kicker">Meeting</p>
          <p>{view.meeting}</p>
        </section>
      ) : null}

      <StopList stops={view.stops} />

      {view.checklist.length ? (
        <ul className="inbox-checklist">
          {view.checklist.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}

      {view.facts.map((fact) => (
        <FactRow key={`${fact.label}:${fact.value}`} label={fact.label} value={fact.value} />
      ))}

      {view.dietary.length ? (
        <section className="card pickup-panel">
          <p className="inbox-kicker">Dietary notes</p>
          {view.dietary.map((note) => <p key={note}>{note}</p>)}
        </section>
      ) : null}

      {view.showTdacChecklist ? (
        <TdacChecklist
          remaining={tdac?.remaining}
          complete={tdac?.complete}
          task={tdac?.task ?? null}
          trip={trip}
          onToggle={onTdac}
        />
      ) : null}

      {view.honesty ? <p className="muted inbox-honesty">{view.honesty}</p> : null}
      {view.source ? (
        <p className="inbox-source">
          Source:{" "}
          {view.sourceHref ? (
            <a href={view.sourceHref} rel="noreferrer" target="_blank">{view.source}</a>
          ) : view.source}
        </p>
      ) : null}

      <AlertActions
        view={view}
        unread={unread}
        confirmNote={confirmNote}
        onConfirmTest={onConfirmTest}
        onSeen={onSeen}
      />
    </article>
  );
}

export function UpdatesPage() {
  const { id } = useParams();
  const decodedId = id ? decodeURIComponent(id) : undefined;
  const { trip } = useTrip();
  const [alerts, setAlerts] = useState<InboxAlert[]>([]);
  const [tdac, setTdac] = useState<{ remaining?: number; complete?: boolean; task?: { expectedTravellerKeys: string[]; completedTravellerKeys: string[] } } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmNote, setConfirmNote] = useState<string | null>(null);
  const deviceId = readDeviceId();

  async function reload() {
    try {
      const data = await fetchUpdates();
      setAlerts((data.alerts as InboxAlert[]) ?? []);
      setTdac(await fetchTdac() as typeof tdac);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Updates are unavailable.");
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  const visible = alerts.filter((row) => row.deliveryMode === "live" || row.category === "test");
  const selected = decodedId && decodedId !== "tdac"
    ? visible.find((row) => row.id === decodedId)
    : decodedId === "tdac"
      ? visible.find((row) => row.category === "tdac")
      : undefined;
  const unreadOf = (alert: InboxAlert) => alert.status === "active" && !alert.seenByDeviceIds.includes(deviceId);

  useEffect(() => {
    if (!selected || !unreadOf(selected)) return;
    void markUpdateSeen(selected.id).then(() => reload()).catch(() => undefined);
  }, [selected?.id]);

  const needs = visible.filter((row) => row.status === "active" && unreadOf(row));
  const restActive = visible.filter((row) => row.status === "active" && !unreadOf(row));
  const earlier = visible.filter((row) => row.status !== "active");
  const showTdacShortcut = tdac && !tdac.complete && !visible.some((row) => row.category === "tdac" && row.status === "active");

  if (decodedId) {
    return (
      <main className="page inbox-page">
        <SmartBack fallback="/updates">All updates</SmartBack>
        {error ? <p className="banner warn">{error}</p> : null}
        {selected ? (
          <AlertDetail
            alert={selected}
            unread={unreadOf(selected)}
            tdac={tdac}
            confirmNote={confirmNote}
            onSeen={() => void markUpdateSeen(selected.id).then(() => reload())}
            onConfirmTest={() => {
              void confirmTestPush()
                .then((data) => {
                  setConfirmNote(String(data.notice ?? "Test confirmed on this phone."));
                  return reload();
                })
                .catch((err: Error) => setConfirmNote(err.message));
            }}
            onTdac={(key, done) => {
              void saveTdac({ travellerKey: key, complete: done }).then(() => reload());
            }}
          />
        ) : decodedId === "tdac" && tdac ? (
          <article className="inbox-detail important">
            <p className="inbox-kicker">Arrival card</p>
            <h1 className="page-title">Thailand Digital Arrival Card</h1>
            <TdacChecklist
              remaining={tdac.remaining}
              complete={tdac.complete}
              task={tdac.task ?? null}
              trip={trip}
              onToggle={(key, done) => void saveTdac({ travellerKey: key, complete: done }).then(() => reload())}
            />
            <p className="muted inbox-honesty">Opening the official website is not completion.</p>
            <p>
              <a className="btn" href={TDAC_OFFICIAL_URL} rel="noreferrer" target="_blank">Official TDAC portal</a>
            </p>
          </article>
        ) : (
          <p>That update is not on this phone.{trip ? " It may have expired, or it was for a different device." : ""}</p>
        )}
      </main>
    );
  }

  return (
    <main className="page inbox-page">
      <h1 className="page-title">Updates</h1>
      <p className="lead">
        {needs.length ? `${needs.length} new. ` : ""}
        Opening one does not mark a booking paid or a form complete.
      </p>
      {error ? <p className="banner warn">{error}</p> : null}

      {showTdacShortcut ? (
        <Link className="inbox-row important unread" to="/updates/tdac">
          <span className="inbox-rail" aria-hidden="true" />
          <span className="inbox-row-copy">
            <span className="inbox-kicker">Arrival card</span>
            <span className="inbox-title">Thailand Digital Arrival Card</span>
            <span className="inbox-line">{tdac?.remaining} traveller{(tdac?.remaining ?? 0) === 1 ? "" : "s"} still need a card</span>
          </span>
          <span className="inbox-when">New</span>
        </Link>
      ) : null}

      {needs.length === 0 && restActive.length === 0 && earlier.length === 0 && !showTdacShortcut ? (
        <p className="muted">Nothing waiting. Missed lock-screen messages still land here.</p>
      ) : null}

      {needs.length ? (
        <>
          <h2 className="section-title">New</h2>
          {needs.map((alert) => (
            <InboxRow key={alert.id} alert={alert} unread={unreadOf(alert)} />
          ))}
        </>
      ) : null}

      {restActive.length ? (
        <>
          <h2 className="section-title">Still relevant</h2>
          {restActive.map((alert) => (
            <InboxRow key={alert.id} alert={alert} unread={unreadOf(alert)} />
          ))}
        </>
      ) : null}

      {earlier.length ? (
        <>
          <h2 className="section-title">Earlier</h2>
          {earlier.map((alert) => (
            <InboxRow key={alert.id} alert={alert} unread={false} />
          ))}
        </>
      ) : null}
    </main>
  );
}
